import React from 'react';
import type { Waiting } from '../../types/waiting';
import type { WaitingAction } from './WaitingActionButtons';
import { maskPhone } from '../../utils/phone';
import { formatTime } from '../../utils/date';
import WaitingStatusBadge from './WaitingStatusBadge';
import WaitingActionButtons from './WaitingActionButtons';
import styles from './WaitingCard.module.css';

const LANG_FLAG: Record<string, { src: string; label: string }> = {
  en: { src: 'https://flagcdn.com/w40/us.png', label: 'EN' },
  ja: { src: 'https://flagcdn.com/w40/jp.png', label: 'JP' },
  zh: { src: 'https://flagcdn.com/w40/cn.png', label: 'CN' },
};

interface WaitingCardProps {
  waiting: Waiting;
  onAction: (waitingId: string, action: WaitingAction) => Promise<void>;
  disabled?: boolean;
}

export default function WaitingCard({ waiting, onAction, disabled }: WaitingCardProps) {
  const isInactive =
    waiting.status === 'SEATED' ||
    waiting.status === 'NO_SHOW' ||
    waiting.status === 'CANCELLED';
  const isTakeout = waiting.orderType === 'TAKEOUT';

  return (
    <div className={`${styles.card} ${isInactive ? styles.inactive : ''}`}>
      <div className={styles.topRow}>
        <div className={styles.waitingNumber}>#{waiting.waitingNumber}</div>
        <div className={styles.info}>
          <div className={styles.phone}>{maskPhone(waiting.customerPhone)}</div>
          <div className={styles.meta}>
            {isTakeout ? (
              <span className={styles.takeoutTag}>포장</span>
            ) : (
              <span>{waiting.partySize}명</span>
            )}
            <span>{formatTime(waiting.createdAt)}</span>
            {waiting.callCount > 0 && (
              <span>호출 {waiting.callCount}회</span>
            )}
          </div>
        </div>
        <div className={styles.badgeArea}>
          {waiting.language && waiting.language !== 'ko' && LANG_FLAG[waiting.language] && (
            <img
              src={LANG_FLAG[waiting.language].src}
              alt={LANG_FLAG[waiting.language].label}
              className={styles.langFlag}
              title={LANG_FLAG[waiting.language].label}
            />
          )}
          <WaitingStatusBadge status={waiting.status} />
        </div>
      </div>

      {(waiting.isReturning || (waiting.noShowCount ?? 0) > 0) && (
        <div className={styles.customerBadges}>
          {waiting.isReturning && (
            <span className={styles.returnBadge}>
              🔁 {(waiting.visitCount ?? 0) + 1}번째 방문
            </span>
          )}
          {(waiting.noShowCount ?? 0) > 0 && (
            <span className={styles.noShowBadge}>
              ⚠️ 노쇼 이력 {waiting.noShowCount}회
            </span>
          )}
        </div>
      )}

      {isTakeout && waiting.orderItems && waiting.orderItems.length > 0 && (
        <div className={styles.orderItems}>
          <div className={styles.orderList}>
            {waiting.orderItems.map((item) => (
              <div key={item.id} className={styles.orderRow}>
                <span className={styles.orderName}>{item.menuName}</span>
                <span className={styles.orderQty}>×{item.quantity}</span>
                <span className={styles.orderPrice}>
                  {(item.price * item.quantity).toLocaleString()}원
                </span>
              </div>
            ))}
          </div>
          <div className={styles.orderTotal}>
            합계 <b>{(waiting.totalAmount ?? 0).toLocaleString()}원</b>
          </div>
        </div>
      )}

      {!isInactive && !disabled && (
        <div className={styles.actions}>
          <WaitingActionButtons
            status={waiting.status}
            waitingId={waiting.id}
            orderType={waiting.orderType}
            onAction={onAction}
          />
        </div>
      )}
    </div>
  );
}
